import React, { useEffect, useMemo, useState } from 'react';

function stringifyValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (_err) {
    return String(value);
  }
}

function isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function parseTypedValue(rawValue, rawType) {
  const value = rawValue;
  const type = String(rawType || '').trim().toUpperCase();

  if (value === null || value === undefined) return value;
  if (type === 'LONG' || type === 'INTEGER' || type === 'INT') {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  if (type === 'DOUBLE' || type === 'FLOAT' || type === 'DECIMAL' || type === 'NUMERIC') {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

function isColumnTripletRow(row) {
  if (!isPlainObject(row)) return false;
  return (
    Object.prototype.hasOwnProperty.call(row, 'columnName')
    && Object.prototype.hasOwnProperty.call(row, 'columnValue')
  );
}

function pivotColumnTriplets(rows) {
  const outRows = [];
  const columns = [];
  const columnSet = new Set();
  let currentRow = {};
  let seenInCurrentRow = new Set();

  const pushCurrent = () => {
    if (Object.keys(currentRow).length > 0) {
      outRows.push(currentRow);
      currentRow = {};
      seenInCurrentRow = new Set();
    }
  };

  rows.forEach((entry) => {
    if (!isColumnTripletRow(entry)) {
      return;
    }

    const columnName = String(entry.columnName || '').trim();
    if (!columnName) {
      return;
    }

    if (!columnSet.has(columnName)) {
      columnSet.add(columnName);
      columns.push(columnName);
    }

    if (seenInCurrentRow.has(columnName)) {
      pushCurrent();
    }

    currentRow[columnName] = parseTypedValue(entry.columnValue, entry.type);
    seenInCurrentRow.add(columnName);
  });

  pushCurrent();
  return { rows: outRows, columns };
}

function normalizeRows(queryResults) {
  if (Array.isArray(queryResults)) {
    if (queryResults.length === 0) return { rows: [], columns: [] };

    if (queryResults.every(isColumnTripletRow)) {
      return pivotColumnTriplets(queryResults);
    }

    if (queryResults.every(isPlainObject)) {
      const columnSet = new Set();
      queryResults.forEach((row) => {
        Object.keys(row).forEach((k) => columnSet.add(k));
      });
      const columns = Array.from(columnSet);
      return { rows: queryResults, columns };
    }

    return {
      rows: queryResults.map((v) => ({ value: v })),
      columns: ['value'],
    };
  }

  if (isPlainObject(queryResults)) {
    const values = Object.values(queryResults);
    const looksLikeRowMap = values.length > 0 && values.every(isPlainObject);
    if (looksLikeRowMap) {
      const rowObjects = values;
      const columnSet = new Set();
      rowObjects.forEach((row) => {
        Object.keys(row).forEach((k) => columnSet.add(k));
      });
      const columns = Array.from(columnSet);
      return { rows: rowObjects, columns };
    }

    return {
      rows: Object.entries(queryResults).map(([key, value]) => ({ key, value })),
      columns: ['key', 'value'],
    };
  }

  if (queryResults !== null && queryResults !== undefined && queryResults !== '') {
    return {
      rows: [{ value: queryResults }],
      columns: ['value'],
    };
  }

  return { rows: [], columns: [] };
}

function renderCell(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return String(value);
    }
    const hasFraction = Math.abs(value % 1) > 0;
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: hasFraction ? 6 : 0,
    });
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        const hasFraction = !Number.isInteger(numeric);
        return numeric.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: hasFraction ? 6 : 0,
        });
      }
    }
    return value;
  }
  if (typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return String(value);
  }
}

export default function DbSourceDetailsModal({ source, index = 1, onClose }) {
  const details = useMemo(() => {
    const metadata = source?.metadata && typeof source.metadata === 'object'
      ? source.metadata
      : {};
    const spotcheck = metadata.db_spotcheck && typeof metadata.db_spotcheck === 'object'
      ? metadata.db_spotcheck
      : {};

    return {
      alteredPrompt: spotcheck.altered_prompt || 'Not available',
      sqlQuery: stringifyValue(spotcheck.sql_query),
      queryResultsRaw: spotcheck.query_results,
      queryResultsText: stringifyValue(spotcheck.query_results),
    };
  }, [source]);

  const tableData = useMemo(
    () => normalizeRows(details.queryResultsRaw),
    [details.queryResultsRaw]
  );

  const [orderedColumns, setOrderedColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [dragColumn, setDragColumn] = useState(null);

  useEffect(() => {
    setOrderedColumns(tableData.columns);
    setSortConfig({ column: null, direction: 'asc' });
    setDragColumn(null);
  }, [tableData.columns]);

  const visibleColumns = useMemo(() => {
    if (!orderedColumns.length) return tableData.columns;
    const existing = orderedColumns.filter((c) => tableData.columns.includes(c));
    const missing = tableData.columns.filter((c) => !existing.includes(c));
    return [...existing, ...missing];
  }, [orderedColumns, tableData.columns]);

  const sortedRows = useMemo(() => {
    const rows = [...tableData.rows];
    const { column, direction } = sortConfig;
    if (!column) return rows;

    const toSortValue = (value) => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'boolean') return value ? 1 : 0;
      if (typeof value === 'string') {
        const t = value.trim();
        if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
        return t.toLowerCase();
      }
      return JSON.stringify(value);
    };

    rows.sort((a, b) => {
      const av = toSortValue(a?.[column]);
      const bv = toSortValue(b?.[column]);

      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;

      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return direction === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [tableData.rows, sortConfig]);

  const toggleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column !== column) {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return { column: null, direction: 'asc' };
    });
  };

  const moveColumn = (fromColumn, toColumn) => {
    if (!fromColumn || !toColumn || fromColumn === toColumn) return;
    setOrderedColumns((prev) => {
      const base = prev.length ? prev : tableData.columns;
      const cols = base.filter((c) => tableData.columns.includes(c));
      const fromIdx = cols.indexOf(fromColumn);
      const toIdx = cols.indexOf(toColumn);
      if (fromIdx < 0 || toIdx < 0) return cols;

      const next = [...cols];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  if (!source) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1060,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className="bg-white rounded shadow-lg d-flex flex-column"
        style={{ width: '90vw', maxWidth: 980, height: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <span className="fw-bold">
            <i className="bi bi-database me-2" />
            DB Source Spot-check {index}
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" aria-label="Close" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="p-3 overflow-auto" style={{ fontSize: '0.9rem' }}>
          <div className="mb-3">
            <div className="fw-semibold mb-1">1. Altered Prompt Fed to DB KB</div>
            <pre className="bg-light border rounded p-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {details.alteredPrompt}
            </pre>
          </div>

          <div className="mb-3">
            <div className="fw-semibold mb-1">2. Generated SQL Query</div>
            <pre className="bg-light border rounded p-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {details.sqlQuery}
            </pre>
          </div>

          <div>
            <div className="fw-semibold mb-1">3. Query Results Returned</div>
            {tableData.rows.length > 0 && tableData.columns.length > 0 ? (
              <div className="border rounded bg-light p-2">
                <div className="text-muted small mb-2">
                  Click a column header to sort. Drag headers to reorder columns.
                </div>
                <div className="table-responsive" style={{ maxHeight: '46vh' }}>
                  <table className="table table-sm table-striped table-bordered mb-0 align-middle">
                    <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        {visibleColumns.map((col) => (
                          <th
                            key={col}
                            draggable
                            onDragStart={() => setDragColumn(col)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              moveColumn(dragColumn, col);
                              setDragColumn(null);
                            }}
                            onDragEnd={() => setDragColumn(null)}
                            onClick={() => toggleSort(col)}
                            style={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                            title="Click to sort, drag to reorder"
                          >
                            <span>{col}</span>
                            <span className="ms-1 text-muted">
                              {sortConfig.column === col
                                ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                                : '↕'}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row, i) => (
                        <tr key={i}>
                          {visibleColumns.map((col) => (
                            <td key={col} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {renderCell(row[col])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <pre className="bg-light border rounded p-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {details.queryResultsText}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
